<template>
  <el-dialog
    title="举报内容"
    v-model="visible"
    :before-close="handleClose"
    width="400px"
  >
    <div class="report-content">
      <el-form :model="reportForm" label-position="top">
        <el-form-item label="举报原因">
          <el-select 
            v-model="reportForm.reason" 
            placeholder="请选择举报原因"
            @change="handleReasonChange"
            style="width: 100%"
          >
            <el-option label="垃圾广告" value="垃圾广告"></el-option>
            <el-option label="色情低俗" value="色情低俗"></el-option>
            <el-option label="政治敏感" value="政治敏感"></el-option>
            <el-option label="人身攻击" value="人身攻击"></el-option>
            <el-option label="侵犯隐私" value="侵犯隐私"></el-option>
            <el-option label="其他" value="other"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item v-if="showCustomReason" label="请输入举报原因">
          <el-input
            v-model="reportForm.customReason"
            type="textarea"
            :rows="4"
            placeholder="请输入具体的举报原因"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
    </div>
    
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :disabled="!canSubmit">提交</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { ref, reactive, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { addReport } from '@/user/api/userApi';

interface ReportFormData {
  targetType: number;
  targetId: number;
  reason: string;
  customReason?: string;
}

export default {
  name: 'ReportModal',
  setup() {
    const reportForm = reactive<ReportFormData>({
      targetType: 1,
      targetId: 0,
      reason: '',
      customReason: undefined
    });

    const visible = ref(false);
    const showCustomReason = ref(false);

    // 计算属性：判断是否可以提交
    const canSubmit = computed(() => {
      if (!reportForm.reason) return false;
      
      if (reportForm.reason === 'other') {
        return reportForm.customReason && reportForm.customReason.trim().length > 0;
      }
      
      return true;
    });

    // 打开举报对话框
    const openReportDialog = (targetType: number, targetId: number) => {
      reportForm.targetType = targetType;
      reportForm.targetId = targetId;
      reportForm.reason = '';
      reportForm.customReason = undefined;
      showCustomReason.value = false;
      visible.value = true;
    };

    // 处理原因选择变化
    const handleReasonChange = (value: string) => {
      if (value === 'other') {
        showCustomReason.value = true;
      } else {
        showCustomReason.value = false;
      }
    };

    // 提交举报
    const handleSubmit = async () => {
      if (!canSubmit.value) {
        ElMessage.warning('请填写举报原因');
        return;
      }

      try {
        // 构建提交数据
        let reasonValue = '';
        if (reportForm.reason === 'other') {
          // 如果选择了"other"，使用自定义原因
          reasonValue = reportForm.customReason?.trim() || '';
        } else {
          // 否则使用选择的原因
          reasonValue = reportForm.reason;
        }

        const submitData = {
          targetType: reportForm.targetType,
          targetId: reportForm.targetId,
          reason: reasonValue
        };

        console.log('Submitting report:', submitData); // 添加调试日志

        const response = await addReport(submitData);
        console.log('Full response:', response); // 输出完整响应

        // 正确处理响应数据 - Axios响应结构为 { data: { code, msg, data }, ... }
        const responseData = response.data;
        console.log('Response data:', responseData);

        if (responseData && typeof responseData === 'object') {
          if (responseData.code === 1) {
            ElMessage.success('举报提交成功');
            visible.value = false;
          } else {
            // 显示后端返回的实际错误信息
            const errorMsg = responseData.msg || responseData.message || '举报提交失败';
            ElMessage.error(errorMsg);
            console.error('举报提交失败:', responseData);
          }
        } else {
          // 响应不是对象的情况
          ElMessage.error('举报提交失败，响应格式异常');
          console.error('响应data不是预期的对象格式:', response.data);
        }
      } catch (error: any) {
        console.error('举报提交失败:', error);
        // 检查是否有响应信息
        if (error.response) {
          console.error('后端响应错误信息:', error.response.data);
          const errorMsg = error.response.data?.msg || error.response.data?.message || '举报提交失败';
          ElMessage.error(errorMsg);
        } else if (error.request) {
          console.error('请求发送但没有响应:', error.request);
          ElMessage.error('网络错误，无法提交举报');
        } else {
          console.error('请求配置错误:', error.message);
          ElMessage.error('请求配置错误');
        }
      }
    };

    // 取消举报
    const handleCancel = () => {
      visible.value = false;
    };

    // 关闭对话框
    const handleClose = () => {
      visible.value = false;
    };

    return {
      reportForm,
      visible,
      showCustomReason,
      canSubmit,
      openReportDialog,
      handleReasonChange,
      handleSubmit,
      handleCancel,
      handleClose
    };
  }
};
</script>

<style scoped>
.report-content {
  padding: 10px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>